import jsPDF from 'jspdf';

export function renderCanvasToPdf({
  pdf,
  canvas,
  tableContentElement,
  capturedHeight,
  topMargin,
  leftMargin,
  rightMargin,
  bottomMargin,
  followingTopMargin,
}: RenderCanvasToPdfParams): number {
  const layout = buildPdfCanvasLayout({
    pdf,
    topMargin,
    leftMargin,
    rightMargin,
    bottomMargin,
    followingTopMargin,
  });

  const tablePaginationMetrics = tableContentElement
    ? getTablePaginationMetrics(tableContentElement)
    : null;

  if (tablePaginationMetrics) {
    return renderTableCanvasByRows({
      pdf,
      canvas,
      capturedHeight: capturedHeight ?? canvas.height,
      layout,
      tablePaginationMetrics,
    });
  }

  return renderCanvasOnSinglePdfPage({pdf, canvas, layout});
}

function getTablePaginationMetrics(contentElement: HTMLElement): TablePaginationMetrics | null {
  const headerElement = contentElement.querySelector('thead') as HTMLElement | null;
  const rowElements = Array.from(contentElement.querySelectorAll('tbody tr')) as HTMLElement[];

  if (!headerElement || rowElements.length === 0) {
    return null;
  }

  const contentRect = contentElement.getBoundingClientRect();
  const getTop = (element: HTMLElement) => element.getBoundingClientRect().top - contentRect.top;

  const headerTop = getTop(headerElement);
  const headerHeight = headerElement.getBoundingClientRect().height;
  const rowBounds = rowElements.map((rowElement) => ({
    top: getTop(rowElement),
    height: rowElement.getBoundingClientRect().height,
  }));

  if (headerHeight <= 0 || rowBounds.length === 0) {
    return null;
  }

  const bodyTopGap = Math.max(0, rowBounds[0].top - (headerTop + headerHeight));

  return {
    headerTop,
    headerHeight,
    bodyTopGap,
    rowBounds,
  };
}

function getTablePaginationPageSlices(
  tablePaginationMetrics: TablePaginationMetrics,
  canvasScaleY: number,
  maxSourcePageHeights: {
    firstPageMaxSourceHeight: number;
    followingPageMaxSourceHeight: number;
  }
): TableVerticalPageSlice[] | null {
  const headerHeight = tablePaginationMetrics.headerHeight * canvasScaleY;
  const bodyTopGap = tablePaginationMetrics.bodyTopGap * canvasScaleY;

  const scaledRowBounds = tablePaginationMetrics.rowBounds.map((rowBound) => ({
    top: rowBound.top * canvasScaleY,
    height: rowBound.height * canvasScaleY,
  }));

  if (scaledRowBounds.length === 0) {
    return null;
  }

  const slices: TableVerticalPageSlice[] = [];
  let currentRowIndex = 0;
  let pageIndex = 0;

  while (currentRowIndex < scaledRowBounds.length) {
    const maxSourcePageHeight =
      pageIndex === 0
        ? maxSourcePageHeights.firstPageMaxSourceHeight
        : maxSourcePageHeights.followingPageMaxSourceHeight;
    const availableBodyHeight = maxSourcePageHeight - headerHeight - bodyTopGap;

    if (availableBodyHeight <= 0) {
      return null;
    }

    const currentSliceStart = scaledRowBounds[currentRowIndex].top;
    let currentSliceBottom = currentSliceStart + scaledRowBounds[currentRowIndex].height;

    if (currentSliceBottom - currentSliceStart > availableBodyHeight) {
      return null;
    }

    currentRowIndex += 1;

    while (currentRowIndex < scaledRowBounds.length) {
      const rowBound = scaledRowBounds[currentRowIndex];
      const nextSliceBottom = rowBound.top + rowBound.height;

      if (nextSliceBottom - currentSliceStart > availableBodyHeight) {
        break;
      }

      currentSliceBottom = nextSliceBottom;
      currentRowIndex += 1;
    }

    slices.push({
      bodyTop: currentSliceStart / canvasScaleY,
      bodyHeight: (currentSliceBottom - currentSliceStart) / canvasScaleY,
    });

    pageIndex += 1;
  }

  return slices;
}

function buildPdfCanvasLayout({
  pdf,
  topMargin,
  leftMargin,
  rightMargin,
  bottomMargin,
  followingTopMargin = 7,
}: BuildPdfCanvasLayoutParams): PdfCanvasLayout {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const width = pdfWidth - leftMargin - rightMargin;
  const resolvedFollowingPageTop = followingTopMargin ?? topMargin;

  return {
    left: leftMargin,
    width,
    firstPage: {
      top: topMargin,
      height: pdfHeight - topMargin - bottomMargin,
    },
    followingPage: {
      top: resolvedFollowingPageTop,
      height: pdfHeight - resolvedFollowingPageTop - bottomMargin,
    },
  };
}

function renderCanvasOnSinglePdfPage({
  pdf,
  canvas,
  layout,
}: {
  pdf: jsPDF;
  canvas: HTMLCanvasElement;
  layout: PdfCanvasLayout;
}): number {
  const widthScale = layout.width / canvas.width;
  const heightScale = layout.firstPage.height / canvas.height;
  const scale = Math.min(widthScale, heightScale);
  const finalWidth = canvas.width * scale;
  const finalHeight = canvas.height * scale;
  const startX = layout.left + (layout.width - finalWidth) / 2;

  pdf.addImage(
    canvas.toDataURL('image/png'),
    'PNG',
    startX,
    layout.firstPage.top,
    finalWidth,
    finalHeight
  );

  return 1;
}

function renderTableCanvasByRows({
  pdf,
  canvas,
  capturedHeight,
  layout,
  tablePaginationMetrics,
}: {
  pdf: jsPDF;
  canvas: HTMLCanvasElement;
  capturedHeight: number;
  layout: PdfCanvasLayout;
  tablePaginationMetrics: TablePaginationMetrics;
}): number {
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const canvasScaleY = canvasHeight / capturedHeight;
  const headerTop = Math.round(tablePaginationMetrics.headerTop * canvasScaleY);
  const headerHeight = Math.max(1, Math.round(tablePaginationMetrics.headerHeight * canvasScaleY));
  const bodyTopGap = Math.max(0, Math.round(tablePaginationMetrics.bodyTopGap * canvasScaleY));
  const boundaryOverlap = Math.max(1, Math.round(canvasScaleY));
  const widthScale = layout.width / canvasWidth;

  const verticalSlices = getTablePaginationPageSlices(tablePaginationMetrics, canvasScaleY, {
    firstPageMaxSourceHeight: layout.firstPage.height / widthScale,
    followingPageMaxSourceHeight: layout.followingPage.height / widthScale,
  });

  if (!verticalSlices || verticalSlices.length === 0) {
    throw new Error('Failed to paginate table content for PDF export');
  }

  for (let pageIndex = 0; pageIndex < verticalSlices.length; pageIndex += 1) {
    if (pageIndex > 0) {
      pdf.addPage();
    }

    const pageTop = pageIndex === 0 ? layout.firstPage.top : layout.followingPage.top;
    const verticalSlice = verticalSlices[pageIndex];
    const sourceBodyY = Math.max(
      headerTop + headerHeight,
      Math.round(verticalSlice.bodyTop * canvasScaleY) - bodyTopGap
    );
    const sourceBodyHeight = Math.min(
      canvasHeight - sourceBodyY,
      bodyTopGap +
        Math.round(verticalSlice.bodyHeight * canvasScaleY) +
        (pageIndex < verticalSlices.length - 1 ? boundaryOverlap : 0)
    );
    const segmentCanvas = document.createElement('canvas');
    segmentCanvas.width = canvasWidth;
    segmentCanvas.height = headerHeight + sourceBodyHeight;

    const segmentContext = segmentCanvas.getContext('2d');
    if (!segmentContext) {
      throw new Error('Failed to create canvas context for PDF export');
    }

    segmentContext.drawImage(
      canvas,
      0,
      headerTop,
      canvasWidth,
      headerHeight,
      0,
      0,
      canvasWidth,
      headerHeight
    );

    segmentContext.drawImage(
      canvas,
      0,
      sourceBodyY,
      canvasWidth,
      sourceBodyHeight,
      0,
      headerHeight,
      canvasWidth,
      sourceBodyHeight
    );

    pdf.addImage(
      segmentCanvas.toDataURL('image/png'),
      'PNG',
      layout.left,
      pageTop,
      layout.width,
      segmentCanvas.height * widthScale
    );
  }

  return verticalSlices.length;
}

// ----------------------------------------------------------
//                      TYPE DEFINITIONS
// ----------------------------------------------------------
type RenderCanvasToPdfParams = {
  pdf: jsPDF;
  canvas: HTMLCanvasElement;
  tableContentElement?: HTMLElement;
  capturedHeight?: number;
  leftMargin: number;
  rightMargin: number;
  topMargin: number;
  bottomMargin: number;
  /** Set top margin on following pages (2nd, 3rd, etc). */
  followingTopMargin?: number;
};

type BuildPdfCanvasLayoutParams = {
  pdf: jsPDF;
  leftMargin: number;
  rightMargin: number;
  topMargin: number;
  bottomMargin: number;
  /** Set top margin on following pages (2nd, 3rd, etc). */
  followingTopMargin?: number;
};

type PdfCanvasLayout = {
  left: number;
  width: number;
  firstPage: PdfPageContentFrame;
  followingPage: PdfPageContentFrame;
};

type TablePaginationMetrics = {
  headerTop: number;
  headerHeight: number;
  bodyTopGap: number;
  rowBounds: TableRowBound[];
};

type TableVerticalPageSlice = {
  bodyTop: number;
  bodyHeight: number;
};

type PdfPageContentFrame = {
  top: number;
  height: number;
};

type TableRowBound = {
  top: number;
  height: number;
};
